import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import ScrollToTop from "../ScrollToTop";
import { convertCartToOrder, loadCart } from "../../data/cart";
import {
  calculateDeliveryForCart,
  getAddressesByType,
  getDefaultAddressByType,
  loadCustomerAddresses,
  loadDeliveryZones,
  saveCustomerAddress,
  validateProductZoneRestrictions,
} from "../../data/delivery";
import { loadDrivers } from "../../data/drivers";
import { useAuth } from "../../context/AuthContext";

const paymentMethods = [
  {
    id: "card",
    title: "Carte bancaire",
    description: "Paiement securise par carte Visa, Mastercard ou CB.",
  },
  {
    id: "paypal",
    title: "PayPal",
    description: "Reglez votre commande via votre compte PayPal.",
  },
  {
    id: "cash",
    title: "Paiement a la livraison",
    description: "Paiement en especes ou par carte a reception selon disponibilite.",
  },
];

const formatPrice = (value) => `${Number(value || 0).toFixed(2)} EUR`;

const ShopCheckOut = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const profileId = user?.id || "";
  const [loaderStatus, setLoaderStatus] = useState(true);
  const [cart, setCart] = useState({ id: "", items: [], reservedUntil: "" });
  const [deliveryZones, setDeliveryZones] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [selectedDriverId, setSelectedDriverId] = useState("");
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedBillingAddressId, setSelectedBillingAddressId] = useState("");
  const [selectedShippingAddressId, setSelectedShippingAddressId] = useState("");
  const [useSameAddress, setUseSameAddress] = useState(true);
  const [selectedPayment, setSelectedPayment] = useState(paymentMethods[0].id);
  const [billingCustomer, setBillingCustomer] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    address: "",
    postcode: "",
    city: "",
    country: "France",
  });
  const [shippingCustomer, setShippingCustomer] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    address: "",
    postcode: "",
    city: "",
    country: "France",
    note: "",
  });
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchCart = async () => {
      const [loadedCart, loadedZones, loadedDrivers, addresses] = await Promise.all([
        loadCart(),
        loadDeliveryZones(),
        loadDrivers(),
        loadCustomerAddresses(profileId),
      ]);
      if (!isMounted) {
        return;
      }
      setCart(loadedCart);
      setDeliveryZones(loadedZones);
      setDrivers(loadedDrivers);
      setSavedAddresses(addresses);
      const defaultBilling = getDefaultAddressByType(addresses, "billing");
      const defaultShipping =
        getDefaultAddressByType(addresses, "shipping") || defaultBilling;

      if (defaultBilling) {
        setSelectedBillingAddressId(defaultBilling.id);
        setBillingCustomer((current) => ({
          ...current,
          firstName:
            defaultBilling.full_name.split(" ").slice(0, -1).join(" ") ||
            defaultBilling.full_name,
          lastName: defaultBilling.full_name.split(" ").slice(-1).join(" "),
          phone: defaultBilling.phone || current.phone,
          address: defaultBilling.line1 || current.address,
          postcode: defaultBilling.postcode || current.postcode,
          city: defaultBilling.city || current.city,
          country: defaultBilling.country || current.country,
        }));
      }

      if (defaultShipping) {
        setSelectedShippingAddressId(defaultShipping.id);
        setShippingCustomer((current) => ({
          ...current,
          firstName:
            defaultShipping.full_name.split(" ").slice(0, -1).join(" ") ||
            defaultShipping.full_name,
          lastName: defaultShipping.full_name.split(" ").slice(-1).join(" "),
          phone: defaultShipping.phone || current.phone,
          address: defaultShipping.line1 || current.address,
          postcode: defaultShipping.postcode || current.postcode,
          city: defaultShipping.city || current.city,
          country: defaultShipping.country || current.country,
        }));
      }
      setLoaderStatus(false);
    };

    fetchCart();

    return () => {
      isMounted = false;
    };
  }, [profileId]);

  const subtotal = useMemo(
    () =>
      cart.items.reduce(
        (sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 0),
        0
      ),
    [cart.items]
  );

  const effectiveShippingCustomer = useMemo(
    () => (useSameAddress ? { ...billingCustomer, note: shippingCustomer.note } : shippingCustomer),
    [billingCustomer, shippingCustomer, useSameAddress]
  );

  const deliveryComputation = useMemo(
    () =>
      calculateDeliveryForCart({
        postcode: effectiveShippingCustomer.postcode,
        subtotal,
        items: cart.items,
        zones: deliveryZones,
      }),
    [cart.items, deliveryZones, effectiveShippingCustomer.postcode, subtotal]
  );

  const productZoneValidation = useMemo(
    () =>
      validateProductZoneRestrictions(
        cart.items,
        deliveryComputation.zone?.id || ""
      ),
    [cart.items, deliveryComputation.zone?.id]
  );

  const orderedDrivers = useMemo(
    () =>
      [...drivers]
        .filter((d) => d.isActive)
        .sort((a, b) => (Number(a.position) || 0) - (Number(b.position) || 0)),
    [drivers]
  );

  const selectedDriver =
    orderedDrivers.find((d) => d.id === selectedDriverId) || orderedDrivers[0] || null;
  const deliveryPrice =
    deliveryComputation.fee === null
      ? 0
      : Number(deliveryComputation.fee || 0);
  const total = subtotal + deliveryPrice;

  const handleCustomerChange = (scope) => (event) => {
    const { name, value } = event.target;
    if (scope === "billing") {
      setBillingCustomer((current) => ({ ...current, [name]: value }));
      return;
    }
    setShippingCustomer((current) => ({ ...current, [name]: value }));
  };

  const fillCustomerFromAddress = (address, scope) => {
    if (!address) {
      return;
    }
    const parts = address.full_name.split(" ");
    const nextState = (current) => ({
      ...current,
      firstName: parts.slice(0, -1).join(" ") || address.full_name,
      lastName: parts.slice(-1).join(" "),
      phone: address.phone || "",
      address: address.line1 || "",
      postcode: address.postcode || "",
      city: address.city || "",
      country: address.country || "France",
    });

    if (scope === "billing") {
      setBillingCustomer(nextState);
      return;
    }

    setShippingCustomer(nextState);
  };

  const handleSelectSavedAddress = (scope) => (event) => {
    const nextId = event.target.value;
    if (scope === "billing") {
      setSelectedBillingAddressId(nextId);
    } else {
      setSelectedShippingAddressId(nextId);
    }
    const address = savedAddresses.find((item) => item.id === nextId);
    fillCustomerFromAddress(address, scope);
  };

  const handleConfirmOrder = async () => {
    setSaving(true);
    setError("");
    setMessage("");

    try {
      if (!deliveryComputation.eligible) {
        throw new Error(
          deliveryComputation.message ||
            "Cette adresse n'est pas eligible a la livraison."
        );
      }

      if (!productZoneValidation.allowed) {
        throw new Error(productZoneValidation.message);
      }

      const billingFullName =
        `${billingCustomer.firstName} ${billingCustomer.lastName}`.trim() ||
        "Client Almoune";
      const shippingFullName =
        `${effectiveShippingCustomer.firstName} ${effectiveShippingCustomer.lastName}`.trim() ||
        billingFullName;

      const savedBillingAddress = await saveCustomerAddress(profileId, {
        id: selectedBillingAddressId || undefined,
        label: "Facturation",
        address_type: "billing",
        full_name: billingFullName,
        phone: billingCustomer.phone,
        line1: billingCustomer.address,
        line2: "",
        postcode: billingCustomer.postcode,
        city: billingCustomer.city,
        country: billingCustomer.country,
        is_default: true,
      });

      const savedShippingAddress = await saveCustomerAddress(profileId, {
        id: useSameAddress ? undefined : selectedShippingAddressId || undefined,
        label: "Livraison",
        address_type: "shipping",
        full_name: shippingFullName,
        phone: effectiveShippingCustomer.phone,
        line1: effectiveShippingCustomer.address,
        line2: "",
        postcode: effectiveShippingCustomer.postcode,
        city: effectiveShippingCustomer.city,
        country: effectiveShippingCustomer.country,
        is_default: true,
      });

      const refreshedAddresses = await loadCustomerAddresses(profileId);
      setSavedAddresses(refreshedAddresses);
      setSelectedBillingAddressId(savedBillingAddress.id);
      setSelectedShippingAddressId(savedShippingAddress.id);

      const order = await convertCartToOrder({
        clientName: billingFullName,
        clientPhone: billingCustomer.phone,
        clientEmail: billingCustomer.email,
        billingAddressId: savedBillingAddress.id,
        shippingAddressId: savedShippingAddress.id,
        deliveryZoneId: deliveryComputation.zone?.id || null,
        deliveryDriverId: selectedDriver?.id || null,
        deliveryDriverName: selectedDriver?.nom || "",
        deliveryType: `${selectedDriver?.nom || "Livreur"} - ${deliveryComputation.zone?.name || "Zone"}`
          .trim(),
        deliveryFee: deliveryPrice,
        paymentMethod:
          paymentMethods.find((method) => method.id === selectedPayment)?.title ||
          selectedPayment,
        note: `${shippingCustomer.note}\nPaiement: ${selectedPayment}`.trim(),
      });

      setMessage(`Commande ${order.orderNumber} creee avec succes.`);
      setTimeout(() => {
        navigate("/MyAcconutPaymentMethod");
      }, 1200);
    } catch (checkoutError) {
      setError(checkoutError.message);
    } finally {
      setSaving(false);
    }
  };

  const billingAddresses = useMemo(
    () => getAddressesByType(savedAddresses, "billing"),
    [savedAddresses]
  );
  const shippingAddresses = useMemo(
    () => getAddressesByType(savedAddresses, "shipping"),
    [savedAddresses]
  );

  return (
    <div>
      {loaderStatus ? (
        <div className="container py-8">
          <div className="alert alert-light border mb-0">
            Chargement du paiement...
          </div>
        </div>
      ) : (
        <>
          <ScrollToTop />
          <section className="mb-lg-14 mb-8 mt-8">
            <div className="container">
              <div className="row">
                <div className="col-12">
                  <div className="mb-8">
                    <h1 className="fw-bold mb-1">Paiement</h1>
                    <p className="mb-0 text-muted">
                      Finalisez votre commande Almoune et convertissez le stock reserve en commande.
                    </p>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-lg-7 col-md-12">
                  <div className="card border-0 shadow-sm mb-4">
                    <div className="card-body p-5">
                      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-4">
                        <div>
                          <h5 className="mb-1">Coordonnees client</h5>
                          <p className="small text-muted mb-0">
                            Adresse de facturation, livraison et informations de contact.
                          </p>
                        </div>
                        <Link to="/MyAccountSignIn" className="small text-primary">
                          Deja client ? Se connecter
                        </Link>
                      </div>

                      {billingAddresses.length ? (
                        <div className="mb-4">
                          <label className="form-label">Adresse de facturation</label>
                          <select
                            className="form-select"
                            value={selectedBillingAddressId}
                            onChange={handleSelectSavedAddress("billing")}
                          >
                            <option value="">Nouvelle adresse</option>
                            {billingAddresses.map((address) => (
                              <option key={address.id} value={address.id}>
                                {address.label} - {address.line1}, {address.postcode} {address.city}
                              </option>
                            ))}
                          </select>
                        </div>
                      ) : null}

                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label">Prenom</label>
                          <input type="text" name="firstName" className="form-control" value={billingCustomer.firstName} onChange={handleCustomerChange("billing")} />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Nom</label>
                          <input type="text" name="lastName" className="form-control" value={billingCustomer.lastName} onChange={handleCustomerChange("billing")} />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Telephone</label>
                          <input type="text" name="phone" className="form-control" value={billingCustomer.phone} onChange={handleCustomerChange("billing")} />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">E-mail</label>
                          <input type="email" name="email" className="form-control" value={billingCustomer.email} onChange={handleCustomerChange("billing")} />
                        </div>
                        <div className="col-12">
                          <label className="form-label">Adresse de facturation</label>
                          <input type="text" name="address" className="form-control" value={billingCustomer.address} onChange={handleCustomerChange("billing")} />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label">Code postal</label>
                          <input type="text" name="postcode" className="form-control" value={billingCustomer.postcode} onChange={handleCustomerChange("billing")} />
                        </div>
                        <div className="col-md-5">
                          <label className="form-label">Ville</label>
                          <input type="text" name="city" className="form-control" value={billingCustomer.city} onChange={handleCustomerChange("billing")} />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label">Pays</label>
                          <input type="text" name="country" className="form-control" value={billingCustomer.country} onChange={handleCustomerChange("billing")} />
                        </div>
                        <div className="col-12">
                          <div className="form-check mt-2">
                            <input
                              id="sameAddress"
                              className="form-check-input"
                              type="checkbox"
                              checked={useSameAddress}
                              onChange={(event) => setUseSameAddress(event.target.checked)}
                            />
                            <label className="form-check-label" htmlFor="sameAddress">
                              Utiliser la meme adresse pour la livraison
                            </label>
                          </div>
                        </div>
                        {!useSameAddress ? (
                          <>
                            {shippingAddresses.length ? (
                              <div className="col-12">
                                <label className="form-label">Adresse de livraison</label>
                                <select
                                  className="form-select"
                                  value={selectedShippingAddressId}
                                  onChange={handleSelectSavedAddress("shipping")}
                                >
                                  <option value="">Nouvelle adresse livraison</option>
                                  {shippingAddresses.map((address) => (
                                    <option key={address.id} value={address.id}>
                                      {address.label} - {address.line1}, {address.postcode} {address.city}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            ) : null}
                            <div className="col-md-6">
                              <label className="form-label">Prenom livraison</label>
                              <input type="text" name="firstName" className="form-control" value={shippingCustomer.firstName} onChange={handleCustomerChange("shipping")} />
                            </div>
                            <div className="col-md-6">
                              <label className="form-label">Nom livraison</label>
                              <input type="text" name="lastName" className="form-control" value={shippingCustomer.lastName} onChange={handleCustomerChange("shipping")} />
                            </div>
                            <div className="col-md-6">
                              <label className="form-label">Telephone livraison</label>
                              <input type="text" name="phone" className="form-control" value={shippingCustomer.phone} onChange={handleCustomerChange("shipping")} />
                            </div>
                            <div className="col-12">
                              <label className="form-label">Adresse de livraison</label>
                              <input type="text" name="address" className="form-control" value={shippingCustomer.address} onChange={handleCustomerChange("shipping")} />
                            </div>
                            <div className="col-md-4">
                              <label className="form-label">Code postal livraison</label>
                              <input type="text" name="postcode" className="form-control" value={shippingCustomer.postcode} onChange={handleCustomerChange("shipping")} />
                            </div>
                            <div className="col-md-5">
                              <label className="form-label">Ville livraison</label>
                              <input type="text" name="city" className="form-control" value={shippingCustomer.city} onChange={handleCustomerChange("shipping")} />
                            </div>
                            <div className="col-md-3">
                              <label className="form-label">Pays livraison</label>
                              <input type="text" name="country" className="form-control" value={shippingCustomer.country} onChange={handleCustomerChange("shipping")} />
                            </div>
                          </>
                        ) : null}
                        <div className="col-12">
                          <label className="form-label">Instructions de livraison</label>
                          <textarea
                            name="note"
                            className="form-control"
                            rows="3"
                            value={shippingCustomer.note}
                            onChange={handleCustomerChange("shipping")}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="card border-0 shadow-sm mb-4">
                    <div className="card-body p-5">
                      <h5 className="mb-1">Livreur</h5>
                      <p className="small text-muted mb-4">
                        Selectionnez le transporteur qui livrera votre commande. Les frais sont calcules selon le code postal de votre zone.
                      </p>

                      <div
                        className={`alert ${
                          deliveryComputation.eligible ? "alert-success" : "alert-warning"
                        } border mb-4`}
                      >
                        {deliveryComputation.message || "Saisissez un code postal pour calculer la livraison."}
                      </div>

                      {(Number(deliveryComputation.freshSubtotal || 0) > 0 ||
                        Number(deliveryComputation.frozenSubtotal || 0) > 0) && (
                        <div className="alert alert-light border mb-4">
                          <div className="small text-muted">
                            Sous-total frais: {formatPrice(deliveryComputation.freshSubtotal || 0)}
                          </div>
                          <div className="small text-muted">
                            Sous-total surgeles: {formatPrice(deliveryComputation.frozenSubtotal || 0)}
                          </div>
                          {deliveryComputation.zone ? (
                            <>
                              <div className="small text-muted">
                                Minimum frais zone: {formatPrice(deliveryComputation.zone.minimum_fresh_order || 0)}
                              </div>
                              <div className="small text-muted">
                                Minimum surgeles zone: {formatPrice(deliveryComputation.zone.minimum_frozen_order || 0)}
                              </div>
                            </>
                          ) : null}
                        </div>
                      )}

                      {!productZoneValidation.allowed ? (
                        <div className="alert alert-danger border mb-4">
                          {productZoneValidation.message}
                        </div>
                      ) : null}

                      <div className="row g-3">
                        {orderedDrivers.map((driver) => {
                          const isSelected = selectedDriver?.id === driver.id;
                          const effectivePrice =
                            deliveryComputation.fee === null
                              ? null
                              : Number(deliveryComputation.fee || 0);

                          return (
                            <div className="col-md-6" key={driver.id}>
                              <label
                                className={`border rounded-3 p-4 d-block h-100 ${
                                  isSelected ? "border-success bg-light" : ""
                                }`}
                              >
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="radio"
                                    name="deliveryDriver"
                                    checked={isSelected}
                                    onChange={() => setSelectedDriverId(driver.id)}
                                  />
                                  <span className="form-check-label text-dark fw-semibold">
                                    {driver.nom || driver.name || "Livreur"}
                                  </span>
                                </div>
                                <div className="d-flex align-items-center gap-3 mt-2">
                                  {driver.logo ? (
                                    <img
                                      src={driver.logo}
                                      alt={driver.nom || driver.name}
                                      className="rounded"
                                      style={{ width: 72, height: 36, objectFit: "contain" }}
                                    />
                                  ) : null}
                                  <span className="small text-muted">
                                    {driver.delai || "Livraison par transporteur"}
                                  </span>
                                </div>
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                  <span className="badge bg-success-subtle text-success border border-success-subtle">
                                    {deliveryComputation.zone?.name || "Zone"}
                                  </span>
                                  <span className="fw-bold">
                                    {effectivePrice === null
                                      ? "Code postal requis"
                                      : effectivePrice === 0
                                      ? "Offerte"
                                      : formatPrice(effectivePrice)}
                                  </span>
                                </div>
                              </label>
                            </div>
                          );
                        })}
                        {!orderedDrivers.length ? (
                          <div className="col-12">
                            <div className="alert alert-warning border mb-0">
                              Aucun livreur actif configure. Ajoutez-en un dans les parametres.
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  <div className="card border-0 shadow-sm">
                    <div className="card-body p-5">
                      <h5 className="mb-1">Mode de paiement</h5>
                      <p className="small text-muted mb-4">
                        Selectionnez votre moyen de paiement prefere.
                      </p>

                      <div className="d-grid gap-3">
                        {paymentMethods.map((method) => (
                          <label
                            key={method.id}
                            className={`border rounded-3 p-4 ${
                              selectedPayment === method.id ? "border-success bg-light" : ""
                            }`}
                          >
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="radio"
                                name="paymentMethod"
                                checked={selectedPayment === method.id}
                                onChange={() => setSelectedPayment(method.id)}
                              />
                              <span className="form-check-label text-dark fw-semibold">
                                {method.title}
                              </span>
                            </div>
                            <p className="small text-muted mb-0 mt-2">{method.description}</p>
                          </label>
                        ))}
                      </div>

                      {message ? <div className="alert alert-success mt-4 mb-0">{message}</div> : null}
                      {error ? <div className="alert alert-danger mt-4 mb-0">{error}</div> : null}

                      <div className="d-flex justify-content-between mt-4 flex-wrap gap-2">
                        <Link to="/ShopCart" className="btn btn-outline-dark">
                          Retour au panier
                        </Link>
                          <button type="button" className="btn btn-primary" onClick={handleConfirmOrder} disabled={!cart.items.length || saving || !selectedDriver || !productZoneValidation.allowed}>
                          {saving ? "Creation..." : "Confirmer la commande"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-12 col-md-12 offset-lg-1 col-lg-4">
                  <div className="card shadow-sm border-0">
                    <div className="card-body p-0">
                      <div className="px-4 py-4 border-bottom">
                        <h5 className="mb-1">Votre commande</h5>
                        <p className="small text-muted mb-0">
                          {cart.items.length} articles reserves chez Almoune
                        </p>
                      </div>

                      <ul className="list-group list-group-flush">
                        {cart.items.filter(item => item.product?.type !== 'fresh' && item.product?.type !== 'frozen').map((item) => (
                          <li className="list-group-item px-4 py-3" key={item.id}>
                            <div className="row align-items-center">
                              <div className="col-9">
                                <h6 className="mb-0">{item.title}</h6>
                                <small className="text-muted">
                                  Epicerie • {item.unit} x {item.quantity}
                                </small>
                              </div>
                              <div className="col-3 text-end">
                                <span className="fw-bold">
                                  {formatPrice(item.price * item.quantity)}
                                </span>
                              </div>
                            </div>
                          </li>
                        ))}
                        
                        {cart.items.filter(item => item.product?.type === 'fresh' || item.product?.type === 'frozen').map((item) => (
                          <li className="list-group-item px-4 py-3 bg-light" key={item.id}>
                            <div className="row align-items-center">
                              <div className="col-9">
                                <h6 className="mb-0">{item.title}</h6>
                                <small className="text-muted">
                                  Frais/Surgele • {item.unit} x {item.quantity}
                                </small>
                              </div>
                              <div className="col-3 text-end">
                                <span className="fw-bold">
                                  {formatPrice(item.price * item.quantity)}
                                </span>
                              </div>
                            </div>
                          </li>
                        ))}

                        <li className="list-group-item px-4 py-3">
                          <div className="d-flex justify-content-between mb-2">
                            <span>Sous-total Epicerie</span>
                            <span className="fw-bold">{formatPrice(deliveryComputation.regularSubtotal)}</span>
                          </div>
                          {deliveryComputation.coldSubtotal > 0 && (
                            <div className="d-flex justify-content-between mb-2">
                              <span>Sous-total Frais/Surgeles</span>
                              <span className="fw-bold">{formatPrice(deliveryComputation.coldSubtotal)}</span>
                            </div>
                          )}
                          <div className="d-flex justify-content-between mb-1 mt-3">
                            <span className="text-muted small">Livraison Epicerie</span>
                            <span className="text-muted small">
                              {deliveryComputation.fee === null
                                ? "-"
                                : deliveryComputation.regularFee === 0
                                ? "Offerte"
                                : formatPrice(deliveryComputation.regularFee)}
                            </span>
                          </div>
                          {deliveryComputation.coldSubtotal > 0 && (
                            <div className="d-flex justify-content-between mb-1">
                              <span className="text-muted small">Livraison Frais/Surgeles</span>
                              <span className="text-muted small">
                                {deliveryComputation.fee === null
                                  ? "-"
                                  : deliveryComputation.coldFee === 0
                                  ? "Offerte"
                                  : formatPrice(deliveryComputation.coldFee)}
                              </span>
                            </div>
                          )}
                        </li>

                        <li className="list-group-item px-4 py-3">
                          <div className="d-flex justify-content-between align-items-center fw-bold">
                            <span>Total a payer</span>
                            <span>{formatPrice(total)}</span>
                          </div>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default ShopCheckOut;
